import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ServiceAccount,
  ServiceAccountInput,
  ServiceAccountListQueryInput,
  ServiceAccountUpdateInput,
} from '@cms/models';
import apiClient from './axios';

type ServiceAccountApiShape = ServiceAccount & {
  platformCode?: string;
  tenantId?: string | null;
  tenantName?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

function normalizeServiceAccount(item: ServiceAccountApiShape): ServiceAccount {
  return {
    id: item.id,
    name: item.name ?? '',
    platform_code: item.platform_code ?? item.platformCode ?? '',
    tenant_id: item.tenant_id ?? item.tenantId ?? null,
    tenant_name: item.tenant_name ?? item.tenantName ?? null,
    status: item.status === 'inactive' ? 'inactive' : 'active',
    cookies: item.cookies ?? null,
    username: item.username ?? null,
    password: item.password ?? null,
    notes: item.notes ?? null,
    created_at: item.created_at ?? item.createdAt ?? '',
    updated_at: item.updated_at ?? item.updatedAt ?? '',
    deleted_at: item.deleted_at ?? item.deletedAt ?? null,
  };
}

export const serviceAccountsQueryKeys = {
  all: ['service-accounts'] as const,
  list: (filters?: ServiceAccountListQueryInput) => ['service-accounts', 'list', filters ?? {}] as const,
};

export const serviceAccountsApi = {
  async list(filters?: ServiceAccountListQueryInput): Promise<ServiceAccount[]> {
    const params: Record<string, string> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.platform_code?.trim()) params.platform_code = filters.platform_code.trim();
    if (filters?.tenant_id) params.tenant_id = filters.tenant_id;
    if (filters?.search?.trim()) params.search = filters.search.trim();
    const { data } = await apiClient.get<ServiceAccountApiShape[]>('/service-accounts', { params });
    return Array.isArray(data) ? data.map(normalizeServiceAccount) : [];
  },

  async create(payload: ServiceAccountInput): Promise<ServiceAccount> {
    const { data } = await apiClient.post<ServiceAccountApiShape>('/service-accounts', payload);
    return normalizeServiceAccount(data);
  },

  async update(id: string, payload: ServiceAccountUpdateInput): Promise<ServiceAccount> {
    const { data } = await apiClient.patch<ServiceAccountApiShape>(`/service-accounts/${id}`, payload);
    return normalizeServiceAccount(data);
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/service-accounts/${id}`);
  },
};

export function useServiceAccounts(filters?: ServiceAccountListQueryInput) {
  return useQuery({
    queryKey: serviceAccountsQueryKeys.list(filters),
    queryFn: () => serviceAccountsApi.list(filters),
  });
}

export function useCreateServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ServiceAccountInput) => serviceAccountsApi.create(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: serviceAccountsQueryKeys.all });
    },
  });
}

export function useUpdateServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; payload: ServiceAccountUpdateInput }) =>
      serviceAccountsApi.update(params.id, params.payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: serviceAccountsQueryKeys.all });
    },
  });
}

export function useDeleteServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceAccountsApi.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: serviceAccountsQueryKeys.all });
    },
  });
}
